/**
 * PostHog and Google Tag Manager, each started as far as the visitor's answer
 * allows. See `lib/consent.js` for the answer.
 *
 * - PostHog counts every visitor who has not turned statistics off. Without the
 *   advertising answer it runs in its cookieless mode: nothing stored on the
 *   device, no person followed, no recordings. With it, it remembers the
 *   visitor, joins them to their account and records the visit.
 * - Tag Manager carries Google Analytics and the Meta Pixel, so it starts only
 *   with the advertising answer, telling Google that answer first, as Google's
 *   consent mode expects, and the advert the visit came from when the visitor
 *   accepts on a later page than the one the advert led to.
 * - Tag Manager never starts for a test account or one of the team acting as
 *   someone, so neither Google nor Meta hears about them.
 *
 * Each runs only where the server gives it an identifier, which is the live
 * site alone, so testing never reaches the live figures.
 */
const HANDED_OVER_KEY = 'bindle_handed_over'
const HANDED_OVER_COUNT_MAXIMUM = 100
let gtmStarted = false
let identifiedUser = null
let labels = {}
let landing = {}
let posthog = null
let posthogAdvertising = null
let posthogIdentified = false
let trackingAdvertising = false

function scriptAdd(src) {
  const script = document.createElement('script')
  script.async = true
  script.src = src

  // Note: Tag Manager passes the page's nonce on to its own tags, as its standard snippet does
  const nonce = document.querySelector('script[nonce]')?.nonce
  if (nonce) {
    script.setAttribute('nonce', nonce)
  }
  document.head.appendChild(script)
}

export function trackersStart(trackers, { advertising, statistics }) {
  trackingAdvertising = advertising

  if (trackers.google_tag_manager && advertising && !gtmStarted && !labels.is_test && !labels.done_by_bindle) {
    gtmStarted = true
    window.dataLayer = window.dataLayer || []
    consentTell()
    campaignTell()
    window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' })
    scriptAdd(`${trackers.google_tag_manager.host}/gtm.js?id=${trackers.google_tag_manager.id}`)
  }
  if (!trackers.posthog || !statistics) {
    return
  }
  if (posthogAdvertising === null) {
    posthogAdvertising = advertising
    posthogStart(trackers.posthog, advertising)
  } else if (posthogAdvertising !== advertising) {
    // Note: PostHog cannot switch between remembering a visitor and its cookieless mode, so the page starts again
    window.location.reload()
  }
}

/*
 * Google's consent mode, basic: Google's tags load only once the visitor has
 * accepted, and are told so before they start. Accept covers measuring which
 * advert brought the visitor, never choosing who sees an advert.
 */
function consentTell() {
  function gtag() {
    window.dataLayer.push(arguments)
  }
  gtag('consent', 'default', { ad_personalization: 'denied', ad_storage: 'denied', ad_user_data: 'denied', analytics_storage: 'denied' })
  gtag('consent', 'update', { ad_personalization: 'denied', ad_storage: 'granted', ad_user_data: 'granted', analytics_storage: 'granted' })
}

/*
 * Google Analytics credits a visit to the advert in the address of its first
 * page. A visitor who accepts on a later page no longer has it there, so the
 * page gives Google Analytics the advert's labels the server noted on landing,
 * as the campaign of the visit, once: only before Google Analytics has its
 * cookie, and only where the page's own address carries none. Tag Manager reads
 * `bindle_campaign_<name>`.
 */
function campaignTell() {
  const params = new URLSearchParams(window.location.search)

  if (!landing.utm_source || params.has('utm_source') || params.has('gclid') || document.cookie.split('; ').some(cookie => cookie.startsWith('_ga='))) {
    return
  }
  window.dataLayer.push(Object.fromEntries(['campaign', 'content', 'medium', 'source', 'term'].map(name => [`bindle_campaign_${name}`, landing[name === 'campaign' ? 'utm_campaign' : `utm_${name}`]])))
}

/*
 * Fetched only for a visitor who has not turned statistics off, so nobody who
 * has downloads it. Without the advertising answer it runs cookieless from the
 * start, which stores nothing on the device, not even that it was refused.
 */
async function posthogStart(posthogConfig, advertising) {
  const client = (await import('posthog-js')).default

  client.init(posthogConfig.key, {
    api_host: posthogConfig.host,
    capture_pageview: 'history_change',
    ...(advertising
      ? { person_profiles: 'identified_only', session_recording: { maskAllInputs: true } }
      : { cookieless_mode: 'always', disable_session_recording: true }),
    ui_host: posthogConfig.ui_host,
  })
  posthog = client
  posthog.register(labels)
  posthogIdentify()
}

function posthogIdentify() {
  if (!posthog || !trackingAdvertising) {
    return
  }
  // Note: one of the team acting as someone stays anonymous, so their browser is never joined to that person
  if (identifiedUser && !labels.done_by_bindle) {
    posthog.identify(String(identifiedUser.id), { user_type: identifiedUser.user_type })
    posthogIdentified = true
  } else if (posthogIdentified || posthog.get_property('$user_state') === 'identified') {
    // Note: signing out starts a new anonymous visitor, so the next person on the device is not merged in
    posthog.reset()
    posthogIdentified = false
  }
}

export function trackersIdentify(user, pageLabels, pageLanding) {
  identifiedUser = user ?? null
  labels = pageLabels ?? {}
  landing = pageLanding ?? {}

  // Note: Tag Manager cannot be unloaded, so signing in as a test account, or as someone, after it started starts the page again
  if (gtmStarted && (labels.is_test || labels.done_by_bindle)) {
    window.location.reload()
  }
  posthog?.register(labels)
  posthogIdentify()
}

/**
 * A step only the page sees, such as typing a card: PostHog counts it, and Tag
 * Manager passes it to Google and Meta for a visitor who accepted. Sent at
 * once, since a step such as answering the banner can be followed by the page
 * starting again.
 */
export function trackersCapture(event, names, properties) {
  posthog?.capture(event, { ...properties, ...labels }, { send_instantly: true })
  trackersHandOver({ event, event_id: crypto.randomUUID(), ...names, properties })
}

/**
 * A step for Google and Meta, from the page or from the server, as Tag Manager
 * expects it: one `bindle_ga4` entry per Google Analytics name, one
 * `bindle_meta` per Meta name, each with the step's id so Meta counts it once
 * when the server tells it too.
 */
export function trackersHandOver({ event, event_id, ga4, meta, properties, value }) {
  // Note: a page brought back by the browser's Back button carries its steps again, so the tab remembers those it handed over
  const handedOverIdList = JSON.parse(sessionStorage.getItem(HANDED_OVER_KEY) ?? '[]')

  if (!gtmStarted || handedOverIdList.includes(event_id)) {
    return
  }
  sessionStorage.setItem(HANDED_OVER_KEY, JSON.stringify([...handedOverIdList, event_id].slice(-HANDED_OVER_COUNT_MAXIMUM)))
  const parameters = { ...properties, bindle_event: event, event_id }
  const amount = properties.amount === undefined ? {} : { currency: 'GBP', value: properties.amount }
  // Note: Meta is told what the step is worth to Bindle, as the server tells it, so the two copies it counts once agree
  const metaValue = value === undefined || value === null ? amount : { currency: 'GBP', value }
  const transaction = properties.subscription_id === undefined ? {} : { transaction_id: `booking-${properties.subscription_id}` }

  for (const name of ga4) {
    window.dataLayer.push({ event: 'bindle_ga4', ga4_event: name, ...parameters, ...amount, ...transaction })
  }
  for (const name of meta) {
    window.dataLayer.push({ event: 'bindle_meta', meta_event: name, ...parameters, ...metaValue })
  }
}
